import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  MapPin, Phone, Mail, Clock, Car, Users,
  FileDown, BookMarked, Award, Share2, Copy, Check,
  Gift, TrendingUp, UserPlus, Sparkles, ExternalLink
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const ATELIER_INFO = {
  address: "Atelier-école Sellerie Académie\nIstres, Provence-Alpes-Côte d'Azur",
  hours: "Du lundi au vendredi · 9h–12h et 13h30–17h30",
  phone: "+33 4 65 01 10 66",
  email: "assistance.sellerie@gmail.com",
  parking: "Parking gratuit devant l'atelier",
};

// ────────────────────────────────────────────────────────── PROGRAMME
export const ProgrammeTab = ({ enrollments }) => {
  if (enrollments.length === 0) {
    return (
      <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-xl">
        <BookMarked className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
        <p className="text-[#4A5568]">Tu verras ici le programme détaillé une fois inscrit à une formation.</p>
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="programme-tab">
      {enrollments.map((e) => {
        const course = e.course;
        if (!course) return null;
        return (
          <div key={e.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6 md:p-10">
            <div className="flex items-baseline gap-4 mb-2">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">PROGRAMME</span>
              <span className="h-px flex-1 bg-[#1F2A4A]/10" />
            </div>
            <h3 className="font-light text-2xl md:text-3xl text-[#1F2A4A] mb-2">{course.title}</h3>
            <p className="text-[#4A5568] text-sm font-light mb-6 leading-relaxed">{course.description}</p>
            {course.episodes && course.episodes.length > 0 ? (
              <ol className="space-y-3">
                {course.episodes.map((ep, i) => (
                  <li key={ep.id || i} className="flex gap-4 items-start border-l-2 border-[#C9B896]/30 pl-4 py-1">
                    <span className="font-serif italic text-[#C9B896] text-xl w-10 flex-shrink-0">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-[#1F2A4A]">{ep.title}</p>
                      {ep.description && <p className="text-sm text-[#4A5568] mt-1">{ep.description}</p>}
                      {ep.duration_minutes != null && (
                        <p className="text-xs text-[#C9B896] mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {ep.duration_minutes} min
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-[#4A5568] italic">Le programme détaillé sera communiqué dès le début de la formation.</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────── INFOS PRATIQUES
export const InfosPratiquesTab = () => {
  const blocks = [
    { Icon: MapPin, title: "Atelier", text: ATELIER_INFO.address },
    { Icon: Clock, title: "Horaires", text: ATELIER_INFO.hours },
    { Icon: Phone, title: "Téléphone", text: ATELIER_INFO.phone, href: `tel:${ATELIER_INFO.phone}` },
    { Icon: Mail, title: "Email", text: ATELIER_INFO.email, href: `mailto:${ATELIER_INFO.email}` },
    { Icon: Car, title: "Parking", text: ATELIER_INFO.parking },
  ];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="infos-tab">
      {blocks.map(({ Icon, title, text, href }) => {
        const content = (
          <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6 hover:border-[#C9B896] transition-colors h-full">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C9B896]/15 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#1F2A4A]" strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-1">{title}</p>
                <p className="text-[#1F2A4A] text-sm whitespace-pre-line leading-relaxed">{text}</p>
              </div>
            </div>
          </div>
        );
        return href ? <a key={title} href={href}>{content}</a> : <div key={title}>{content}</div>;
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────── MANUEL
export const ManuelTab = ({ enrollments }) => {
  const [manuals, setManuals] = useState({});

  useEffect(() => {
    Promise.all(enrollments.map(async (e) => {
      if (!e.course?.id) return null;
      try {
        const m = await api.getCourseManual(e.course.id);
        return [e.course.id, m];
      } catch {
        return [e.course.id, null];
      }
    })).then((rows) => {
      const map = {};
      rows.forEach((r) => { if (r) map[r[0]] = r[1]; });
      setManuals(map);
    });
  }, [enrollments]);

  if (enrollments.length === 0) {
    return (
      <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-xl">
        <FileDown className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
        <p className="text-[#4A5568]">Ton manuel de cours sera disponible ici dès le début de ta formation.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="manuel-tab">
      {enrollments.map((e) => {
        const c = e.course;
        if (!c) return null;
        const m = manuals[c.id];
        return (
          <div key={e.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6 flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1F2A4A] to-[#324166] flex items-center justify-center flex-shrink-0">
              <FileDown className="w-6 h-6 text-[#C9B896]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]">{c.title}</p>
              <p className="text-[#1F2A4A] font-medium mt-0.5">
                {m ? m.title : 'Manuel à venir'}
              </p>
              {m?.version && <p className="text-xs text-[#4A5568] mt-0.5">Version {m.version}</p>}
            </div>
            {m?.file_url ? (
              <a href={m.file_url} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full" data-testid={`manuel-download-${c.id}`}>
                  Télécharger <FileDown className="w-4 h-4 ml-2" />
                </Button>
              </a>
            ) : (
              <Badge variant="outline" className="text-[#4A5568]">Bientôt disponible</Badge>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────── BASE DE CONNAISSANCE
const CATEGORY_LABELS = {
  technique: 'Technique', materiaux: 'Matériaux', outils: 'Outils',
  entreprise: 'Entreprise', video: 'Vidéo', glossaire: 'Glossaire',
};

export const KnowledgeBaseTab = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.listKbArticles().then(setArticles).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? articles : articles.filter((a) => a.category === filter);

  if (loading) {
    return <div className="text-center py-12 text-[#4A5568]">Chargement…</div>;
  }

  return (
    <div data-testid="kb-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] font-medium transition-colors ${
            filter === 'all' ? 'bg-[#1F2A4A] text-white' : 'bg-white border border-[#1F2A4A]/15 text-[#1F2A4A] hover:bg-[#1F2A4A]/5'
          }`}
        >
          Toutes ({articles.length})
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
          const count = articles.filter((a) => a.category === key).length;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] font-medium transition-colors ${
                filter === key ? 'bg-[#1F2A4A] text-white' : 'bg-white border border-[#1F2A4A]/15 text-[#1F2A4A] hover:bg-[#1F2A4A]/5'
              }`}
              data-testid={`kb-filter-${key}`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Articles grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-xl">
          <BookMarked className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
          <p className="text-[#4A5568]">Aucun article publié pour le moment.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((a) => (
            <article key={a.id} className="group bg-white border border-[#1F2A4A]/10 rounded-xl overflow-hidden hover:border-[#C9B896] hover:shadow-[0_8px_24px_-12px_rgba(31,42,74,0.15)] transition-all" data-testid={`kb-card-${a.slug}`}>
              {a.cover_url && (
                <div className="aspect-video bg-[#1F2A4A] overflow-hidden">
                  <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896]">
                    {CATEGORY_LABELS[a.category]}
                  </span>
                  {a.youtube_url && <span className="text-[10px] text-[#1F2A4A]/60">· Vidéo</span>}
                </div>
                <h3 className="font-light text-xl text-[#1F2A4A] leading-tight mb-2">{a.title}</h3>
                {a.summary && <p className="text-sm text-[#4A5568] leading-relaxed mb-4 line-clamp-3">{a.summary}</p>}
                <div className="flex items-center gap-3">
                  {a.youtube_url && (
                    <a href={a.youtube_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1F2A4A] underline-offset-4 hover:underline inline-flex items-center gap-1.5">
                      Voir la vidéo <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────── PARRAINAGE
export const ParrainageTab = () => {
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getMyPoints(),
      api.getMyReferrals(),
      api.getMyPointsHistory(),
    ]).then(([s, r, h]) => {
      setSummary(s);
      setReferrals(r.referrals || []);
      setHistory(h);
    }).finally(() => setLoading(false));
  }, []);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  if (loading || !summary) {
    return <div className="text-center py-12 text-[#4A5568]">Chargement…</div>;
  }

  const shareLink = `${window.location.origin}/inscription?ref=${summary.referral_code}`;
  const progressPercent = summary.points_to_next_discount > 0
    ? Math.round(((100 - summary.points_to_next_discount) / 100) * 100)
    : 100;

  return (
    <div className="space-y-6" data-testid="parrainage-tab">
      {/* Hero card */}
      <div className="bg-gradient-to-br from-[#1F2A4A] to-[#324166] text-white rounded-xl p-8 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#C9B896]/15 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-[#C9B896]" />
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">Programme de parrainage</span>
          </div>
          <h3 className="font-light text-3xl md:text-4xl leading-tight mb-2 text-white">
            Invite un ami à devenir<br />
            <span className="font-serif italic text-[#C9B896]">sellier & garnisseur.</span>
          </h3>
          <p className="text-white/70 font-light max-w-xl mt-3">
            Pour chaque ami inscrit : <strong className="text-white">+25 points</strong>. Si l'ami signe une formation : <strong className="text-white">+75 points</strong>.
            <br />Atteins <strong className="text-[#C9B896]">100 points</strong> et bénéficie de <strong className="text-[#C9B896]">10 % de remise</strong> sur ta prochaine formation.
          </p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { value: summary.balance, label: 'Points', sub: 'solde actuel', Icon: Award },
          { value: summary.referrals_count, label: 'Filleuls', sub: 'inscrits', Icon: UserPlus },
          { value: summary.signed_referrals_count, label: 'Signés', sub: 'ont commencé', Icon: TrendingUp },
          { value: `${summary.discount_available_percent}%`, label: 'Remise', sub: 'disponible', Icon: Gift },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid={`points-stat-${i}`}>
            <s.Icon className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none mb-1">{s.value}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">{s.label}</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Progress to next discount */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-sm font-medium text-[#1F2A4A]">
            {summary.points_to_next_discount === 0
              ? 'Bravo ! Tu peux convertir tes points en remise.'
              : `Plus que ${summary.points_to_next_discount} points pour 10 % de remise.`}
          </p>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#C9B896]">
            {summary.balance % 100} / 100 pts
          </p>
        </div>
        <div className="h-2 bg-[#1F2A4A]/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#C9B896] to-[#E0D4BA] transition-all duration-700"
            style={{ width: `${progressPercent}%` }}
            data-testid="points-progress"
          />
        </div>
      </div>

      {/* Share link */}
      <div className="bg-[#FAF7F0] border border-[#C9B896]/30 rounded-xl p-6">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-3 flex items-center gap-2">
          <Share2 className="w-3 h-3" /> Ton code de parrainage
        </p>
        <p className="font-serif italic text-3xl text-[#1F2A4A] mb-4 tracking-wide">
          {summary.referral_code}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            readOnly
            value={shareLink}
            onClick={(e) => e.target.select()}
            className="flex-1 bg-white border border-[#1F2A4A]/15 rounded-full px-5 py-3 text-sm text-[#1F2A4A] font-mono"
            data-testid="referral-link"
          />
          <Button
            onClick={() => handleCopy(shareLink)}
            className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-6"
            data-testid="copy-referral-link"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copié' : 'Copier'}
          </Button>
        </div>
      </div>

      {/* Referrals list */}
      {referrals.length > 0 && (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4">Tes filleuls</p>
          <ul className="divide-y divide-[#1F2A4A]/8">
            {referrals.map((r) => (
              <li key={r.id} className="py-3 flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-[#C9B896]/15 flex items-center justify-center text-[#1F2A4A] text-sm font-medium">
                  {(r.first_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1F2A4A]">{r.first_name} {r.last_name?.charAt(0)}.</p>
                  <p className="text-xs text-[#4A5568]">{r.email}</p>
                </div>
                {r.status === 'signed' ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200">+75 pts · Signé</Badge>
                ) : (
                  <Badge variant="outline" className="text-[#4A5568]">+25 pts · Inscrit</Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <details className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6">
          <summary className="cursor-pointer text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">
            Historique des points ({history.length})
          </summary>
          <ul className="mt-4 divide-y divide-[#1F2A4A]/8">
            {history.map((h) => (
              <li key={h.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <p className="text-[#1F2A4A]">{h.note || h.reason}</p>
                  <p className="text-xs text-[#4A5568]">{format(parseISO(h.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}</p>
                </div>
                <span className={`font-mono text-sm font-medium ${h.delta > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {h.delta > 0 ? '+' : ''}{h.delta} pts
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
